import { useMemo, useCallback } from 'react'
import { Button, Table, Tooltip } from 'antd'
import {
  DatabaseOutlined,
  FileTextFilled,
  MinusOutlined,
  FileSearchOutlined,
  FileExcelOutlined
} from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import PropTypes from 'prop-types'
import queryString from 'query-string'
import dayjs from '../../lib/datetime'
import Filters from '../Forms/Filters'
import usePracticeCycles from '../Common/usePracticeCycles'
import useFilters from '../Cycles/useFilters'
import { ListError } from '../Common'
import { PAGE_SIZE_OPTIONS, PAGE_SIZE } from '../../lib/constants'
import { toPath } from '../../routes'
import { useAsync } from '../../hooks'
import useExportExcel from '../../hooks/useExportExcel'
import { useFetchOrders, useOrders } from '../../redux/slices/orders'
import ShowTotal from '../Common/ShowTotal'

const useStyles = createUseStyles({
  present: {
    color: 'green',
  },
  notPresent: {
    color: 'orange',
  },
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start', // Changed from flex-end to flex-start
    justifyContent: 'space-between', // Changed from flex-start to space-between
  },
  exportContainer: {
    textAlign: 'center',
  },
})

const DEFAULT_FILTERS = {
  fulfilled: 'all',
  showDuplicates: false,
}

const DEFAULT_PAGE = {
  fulfilledPage: 1,
  fulfilledPageSize: PAGE_SIZE,
}

export default function FulfilledOrders({ cycleId, activeTab }) {
  const classes = useStyles() // Call the hook to get class names

  const columns = [
    {
      title: 'Patient Name',
      dataIndex: ['patient'],
      render: (_, { patient }) =>
        patient && patient.id ? (
          <Link to={toPath.patients.patient({ id: `${patient.id}` })}>
            {patient.lastName}, {patient.firstName}
          </Link>
        ) : (
          <>No Patient</>
        ),
    },
    {
      title: 'Patient MRN',
      align: 'right',
      dataIndex: ['patient', 'practiceMrn'],
    },
    {
      title: 'Order Number',
      align: 'right',
      dataIndex: ['orderNumber'],
    },
    {
      title: 'Encounter',
      align: 'right',
      dataIndex: ['encounterId'],
    },
    {
      title: 'Created At',
      dataIndex: ['createdAt'],

      render(text, { createdAt }) {
        return <div>{dayjs(createdAt).format('MMM D, YYYY')}</div>
      },
    },
    {
      title: 'Status',
      align: 'right',
      dataIndex: ['report'],
      render: (_, { report: { eventsSucceeded } }) => {
        if (!eventsSucceeded) return <MinusOutlined />

        return (
          <Tooltip
            title={
              <pre
                style={{
                  maxHeight: '400px',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {eventsSucceeded}
              </pre>
            }
            overlayStyle={{ maxWidth: '600px' }}
            placement="right"
          >
            <DatabaseOutlined style={{ color: 'green', cursor: 'pointer' }} />
          </Tooltip>
        )
      },
    },
    {
      title: 'Errors',
      align: 'right',
      dataIndex: ['report'],
      render: (_, { report: { errorMessages } }) => {
        if (!errorMessages) return <MinusOutlined />

        return (
          <Tooltip
            title={
              <pre
                style={{
                  maxHeight: '400px',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {errorMessages}
              </pre>
            }
            overlayStyle={{ maxWidth: '600px' }}
            placement="right"
          >
            <DatabaseOutlined style={{ color: 'red', cursor: 'pointer' }} />
          </Tooltip>
        )
      },
    },
    {
      title: 'Raw',
      align: 'right',
      dataIndex: ['requestData'],
      render: (_, { requestData }) => {
        if (!requestData) return <MinusOutlined />

        // Format the JSON data for display
        const formattedData = JSON.stringify(requestData, null, 2)

        return (
          <Tooltip
            title={
              <pre
                style={{
                  maxHeight: '400px',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {formattedData}
              </pre>
            }
            overlayStyle={{ maxWidth: '600px' }}
            placement="right"
          >
            <FileTextFilled style={{ cursor: 'pointer' }} />
          </Tooltip>
        )
      },
    },

    {
      title: 'Report',
      key: 'actions',
      render(_text, { reportId, remoteCheckId }) {
        const reportUrl = reportId
          ? `/reports/${reportId}`
          : `/remote-checks/${remoteCheckId}`
        return (
          <span>
            <Link to={reportUrl}>
              <Tooltip
                title={reportId || remoteCheckId ? 'View Report' : 'No Report'}
              >
                <Button
                  disabled={!reportId && !remoteCheckId}
                  type="link"
                  icon={<FileSearchOutlined />}
                />
              </Tooltip>
            </Link>
          </span>
        )
      },
    },
  ]

  // eslint-disable-next-line no-unused-vars
  const [_, { practice, practiceId }] = usePracticeCycles()

  const { conditions, setConditions } = useFilters({
    ...DEFAULT_FILTERS,
    practiceId,
  })

  const { fulfilledPage, fulfilledPageSize } = {
    ...DEFAULT_PAGE,
    ...conditions,
  }

  // this is a general implementation, among them are making sure some keys are
  // on the expected format and to avoid re-render if a key changes in
  // `conditions` but it don't belong to FULFILLED_FILTERS array.
  const memoConditions = useMemo(() => {
    const {
      currentPracticeId,
      deviceTypeId,
      fulfilled,
      medicalProviderId,
      officeLocationId,
      query,
      showDuplicates,
    } = conditions

    return {
      currentPracticeId,
      cycleId,
      deviceTypeId,
      fulfilled,
      medicalProviderId,
      officeLocationId,
      practiceId,
      query,
      // XXX: this is to avoid type warnings when loading queryParams
      showDuplicates: showDuplicates === true || showDuplicates === 'true',
    }

    // NOTE: eslint want to check `conditions` because of pick function, but
    // it's only necessary to refresh `memoConditions` when the ones below are
    // changed:
    //
  }, [conditions, cycleId, practiceId])

  const { error, clearError, pending } = useAsync(
    useFetchOrders(conditions, true),
  )

  const { orders, total } = useOrders(conditions)

  const setFilters = useCallback(
    newConditions =>
      setConditions({
        ...memoConditions,
        ...DEFAULT_FILTERS,
        ...newConditions,
        fulfilledPage: 1, // reset page to the first as all records changed
        fulfilledPageSize,
        activeTab,
      }),
    [setConditions, memoConditions, fulfilledPageSize, activeTab],
  )

  const setPage = useCallback(
    (newPage, newPageSize) =>
      setConditions({
        ...memoConditions,
        ...DEFAULT_PAGE,
        activeTab,
        fulfilledPage: newPage,
        fulfilledPageSize: newPageSize,
      }),
    [activeTab, memoConditions, setConditions],
  )

  const setPageSize = useCallback(
    (newPage, newPageSize) =>
      setConditions({
        ...memoConditions,
        activeTab,
        fulfilledPage: newPage,
        fulfilledPageSize: newPageSize,
      }),
    [activeTab, memoConditions, setConditions],
  )

  const excelExportUrl = useMemo(
    () =>
      queryString.stringifyUrl({
        url: `/reports/orders/`,
        query: {
          cycle_id: cycleId,
          device_type_id: memoConditions.deviceTypeId,
          medical_provider_id: memoConditions.medicalProviderId,
          office_location_id: memoConditions.officeLocationId,
          fulfilled: memoConditions.fulfilled,
          show_duplicates: memoConditions.showDuplicates,
          q: memoConditions.query,
        },
      }),
    [cycleId, memoConditions],
  )

  const [exportingExcel, exportExcel] = useExportExcel(excelExportUrl)

  return (
    <div>
      <div className={classes.container}>
        {error && (
          <ListError description={error.message} onClose={clearError} />
        )}

        <Filters
          defaultValues={DEFAULT_FILTERS}
          currentValues={memoConditions}
          onSubmit={setFilters}
          onChange={setFilters}
          placeholders={{ query: 'Search Order or Patient' }}
          showDeviceType
          showDuplicatesCheck
          showMedicalProvider
          showOfficeLocation
          showOrderStatus
          showSearch
        />

      </div>
      <Table
        rowKey="id"
        columns={columns}
        className={classes.ordersTable}
        dataSource={orders}
        loading={pending}
        pagination={{
          total,
          page: +fulfilledPage,
          pageSize: +fulfilledPageSize,
          onChange: setPage,
          onShowSizeChange: setPageSize,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          showSizeChanger: true,
          showTotal: <ShowTotal orders={orders} total={total} />,
        }}
      />
      {Boolean(orders.length) && (
        <div className={classes.exportContainer}>
          <Button
            loading={exportingExcel}
            onClick={exportExcel}
            icon={<FileExcelOutlined />}
          >
            Export this list to Excel
          </Button>
        </div>
      )}
    </div>
  )
}

FulfilledOrders.propTypes = {
  cycleId: PropTypes.string.isRequired,
  activeTab: PropTypes.string.isRequired,
}
